import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { STORY_CHAPTERS } from "./data";
import { resolveFileUrl } from "./fileUrl";

const Chapter = ({ chapter, idx }) => {
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0.4]);

  return (
    <section ref={ref} className="min-h-[80vh] flex items-center py-24 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto w-full grid lg:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y, opacity }} className="lg:col-span-7">
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono mb-6">
            {chapter.chapter}
          </div>
          <h3
            className="text-[44px] md:text-[72px] leading-[1] tracking-tight text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {chapter.title}
          </h3>
          <p className="mt-8 max-w-[640px] text-[17px] md:text-[19px] leading-[1.7] text-white/65">
            {chapter.body}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {chapter.keywords.map((k) => (
              <span
                key={k}
                className="px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[12px] text-white/60"
              >
                {k}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Decorative side card */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          className="lg:col-span-5"
        >
          <div className="relative aspect-[4/5] rounded-[20px] overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent backdrop-blur-xl">
            {chapter.image && (
              <>
                <img
                  src={resolveFileUrl(chapter.image)}
                  alt={chapter.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: "grayscale(0.85) sepia(0.5) brightness(0.55) contrast(1.05)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/30" />
                <div className="absolute inset-0" style={{
                  background: "radial-gradient(circle at 30% 30%, rgba(230,199,105,0.18), transparent 55%)",
                }} />
              </>
            )}
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center">
                <div className="text-[200px] font-extrabold text-white/[0.08] leading-none select-none" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="absolute inset-0 grid place-items-center">
                  <div className="text-[12px] tracking-[0.4em] uppercase text-[#e6c769]/90 font-mono">
                    {chapter.chapter}
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute top-5 left-5 text-[10px] tracking-[0.3em] uppercase text-white/60 font-mono">
              — fragment
            </div>
            <div className="absolute bottom-5 right-5 text-[10px] tracking-[0.3em] uppercase text-[#e6c769]/80 font-mono">
              {chapter.id}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Story = () => {
  return (
    <section id="story" className="relative py-28">
      {/* Section header */}
      <div className="max-w-[1300px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="flex items-end justify-between flex-wrap gap-6"
        >
          <div>
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">02 / The Story</div>
            <h2
              className="mt-3 text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              An <span className="italic font-medium">autobiography</span><br />
              in chapters.
            </h2>
          </div>
          <div className="max-w-md text-white/55 text-[15px] leading-relaxed">
            Not a résumé. A quiet narrative — told in fragments, scrolled like film. Each chapter a turning point; each turn a lesson held forward.
          </div>
        </motion.div>

        <div className="mt-12 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      </div>

      {/* Chapters */}
      {STORY_CHAPTERS.map((c, i) => (
        <Chapter key={c.id} chapter={c} idx={i} />
      ))}
    </section>
  );
};

export default Story;
